import { useEffect, useState } from 'react';

const DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
const API_VERSION = '2025-01';

export const SHOPIFY_READY = Boolean(DOMAIN && TOKEN);

// becomes true once the real product is found on the store — until then the
// storefront keeps running on the mock catalog + mock cart, then switches over
let catalogLive = false;
export const isCatalogLive = () => SHOPIFY_READY && catalogLive;

export const eur = (value) =>
  '€' + Number(value).toFixed(2).replace('.', ',');

/* ------------------------------------------------------------------ */
/* MOCK CATALOG — replaced by live Shopify data the moment            */
/* REACT_APP_SHOPIFY_STORE_DOMAIN + STOREFRONT token are set in .env  */
/* ------------------------------------------------------------------ */

const CHROME_IMAGES = [
  { url: '/assets/packaging-open.png', alt: '01 / CHROME — studio portrait' },
  { url: '/assets/detail-shell.png', alt: '01 / CHROME — polished shell macro' },
  { url: '/assets/detail-cable.png', alt: '01 / CHROME — braided silver cable' },
  { url: '/assets/hero-earphones.png', alt: '01 / CHROME — campaign still' },
];

export const MOCK_PRODUCTS = [
  {
    id: 'gid://shopify/Product/9001',
    handle: '01-chrome',
    title: '01 / CHROME',
    subtitle: 'Chrome Wired Earphones',
    description:
      'Wired earphones cast in polished chrome. An object first, an earphone second. Weight, metal and uninterrupted sound — engineered to be seen.',
    price: '29.90',
    compareAtPrice: '34.90',
    currency: 'EUR',
    images: CHROME_IMAGES,
    videos: [],
    options: [
      {
        name: 'PACK',
        values: ['JUST 01'],
      },
    ],
    variants: [
      {
        id: 'gid://shopify/ProductVariant/9101',
        title: 'Default Title',
        sku: 'VZ-01C-USBC',
        available: true,
        inventoryQuantity: null,
        price: '29.90',
        compareAtPrice: '34.90',
        selectedOptions: [{ name: 'PACK', value: 'JUST 01' }],
      },
    ],
  },
  {
    id: 'gid://shopify/Product/9002',
    handle: 'two-of-us',
    title: 'TWO OF US',
    subtitle: '2 × 01 / CHROME',
    description: 'Two pairs of 01 / CHROME. One for you, one for them.',
    price: '54.90',
    compareAtPrice: null,
    currency: 'EUR',
    images: CHROME_IMAGES,
    videos: [],
    options: [
      {
        name: 'PACK',
        values: ['TWO OF US'],
      },
    ],
    variants: [
      {
        id: 'gid://shopify/ProductVariant/9201',
        title: 'Default Title',
        sku: 'VZ-01C-2X',
        available: true,
        inventoryQuantity: null,
        price: '54.90',
        compareAtPrice: null,
        selectedOptions: [{ name: 'PACK', value: 'TWO OF US' }],
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* STOREFRONT API — GraphQL layer (activates when credentials exist)  */
/* ------------------------------------------------------------------ */

async function sf(query, variables = {}) {
  const res = await fetch(`https://${DOMAIN}/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data;
}

const PRODUCT_FIELDS = `
  id handle title description
  featuredImage { url altText }
  images(first: 12) { edges { node { url altText } } }
  options { name values }
  variants(first: 50) {
    edges { node {
      id title sku availableForSale
      price { amount currencyCode }
      compareAtPrice { amount }
      selectedOptions { name value }
      image { url altText }
    } }
  }
`;

function normalizeProduct(p) {
  const v0 = p.variants.edges[0]?.node;
  return {
    id: p.id,
    handle: p.handle,
    title: p.title,
    subtitle: '',
    description: p.description,
    price: v0?.price.amount ?? '0',
    compareAtPrice: v0?.compareAtPrice?.amount ?? null,
    currency: v0?.price.currencyCode ?? 'EUR',
    images: p.images.edges.map((e) => ({ url: e.node.url, alt: e.node.altText || p.title })),
    videos: [],
    options: p.options,
    variants: p.variants.edges.map((e) => ({
      id: e.node.id,
      title: e.node.title,
      sku: e.node.sku,
      available: e.node.availableForSale,
      inventoryQuantity: e.node.quantityAvailable,
      price: e.node.price.amount,
      compareAtPrice: e.node.compareAtPrice?.amount ?? null,
      selectedOptions: e.node.selectedOptions,
    })),
  };
}

/* ------------------------------ API ------------------------------- */

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export async function fetchProducts() {
  if (!SHOPIFY_READY) {
    await delay(120);
    return MOCK_PRODUCTS;
  }
  const data = await sf(`{ products(first: 20) { edges { node { ${PRODUCT_FIELDS} } } } }`);
  return data.products.edges.map((e) => normalizeProduct(e.node));
}

export async function getProductByHandle(handle) {
  if (!SHOPIFY_READY) {
    await delay(120);
    return MOCK_PRODUCTS.find((p) => p.handle === handle) || null;
  }
  const data = await sf(
    `query ($handle: String!) { productByHandle(handle: $handle) { ${PRODUCT_FIELDS} } }`,
    { handle }
  );
  if (data.productByHandle) {
    catalogLive = true;
    return normalizeProduct(data.productByHandle);
  }
  // product missing on the live store (e.g. unpublished) — flagged mock for
  // display only, it must never reach the Shopify cart
  const mock = MOCK_PRODUCTS.find((p) => p.handle === handle);
  return mock ? { ...mock, mock: true } : null;
}

const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    lines(first: 50) {
      edges { node {
        id quantity
        merchandise { ... on ProductVariant {
          id title sku
          price { amount }
          product { title handle featuredImage { url altText } }
        } }
      } }
    }
  }
`;

function mapRemoteCart(cart) {
  const cleanTitle = (title, handle) =>
    title !== 'Default Title'
      ? title
      : /two|2-of-us/i.test(handle || '')
        ? '2 × 01 / CHROME'
        : 'JUST 01';
  return {
    cartId: cart.id,
    checkoutUrl: cart.checkoutUrl,
    lines: cart.lines.edges.map((e) => ({
      key: e.node.id,
      quantity: e.node.quantity,
      variant: {
        id: e.node.merchandise.id,
        title: cleanTitle(e.node.merchandise.title, e.node.merchandise.product.handle),
        sku: e.node.merchandise.sku,
        price: e.node.merchandise.price.amount,
      },
      product: {
        title: e.node.merchandise.product.title,
        handle: e.node.merchandise.product.handle,
        image: e.node.merchandise.product.featuredImage?.url || '/assets/packaging-open.png',
      },
    })),
  };
}

function assertCart(payload) {
  if (payload?.userErrors?.length) throw new Error(payload.userErrors[0].message);
  if (!payload?.cart) throw new Error('Shopify cart unavailable');
  return mapRemoteCart(payload.cart);
}

export async function remoteCartCreate(lines) {
  const data = await sf(
    `${CART_FRAGMENT} mutation ($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) { cart { ...CartFields } userErrors { field message } }
    }`,
    { lines }
  );
  return assertCart(data.cartCreate);
}

export async function remoteCartLinesAdd(cartId, lines) {
  const data = await sf(
    `${CART_FRAGMENT} mutation ($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) { cart { ...CartFields } userErrors { field message } }
    }`,
    { cartId, lines }
  );
  return assertCart(data.cartLinesAdd);
}

export async function remoteCartLinesUpdate(cartId, lines) {
  const data = await sf(
    `${CART_FRAGMENT} mutation ($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { ...CartFields } userErrors { field message } }
    }`,
    { cartId, lines }
  );
  return assertCart(data.cartLinesUpdate);
}

export async function remoteCartLinesRemove(cartId, lineIds) {
  const data = await sf(
    `${CART_FRAGMENT} mutation ($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { ...CartFields } userErrors { field message } }
    }`,
    { cartId, lineIds }
  );
  return assertCart(data.cartLinesRemove);
}

export async function remoteCartGet(cartId) {
  const data = await sf(
    `${CART_FRAGMENT} query ($cartId: ID!) { cart(id: $cartId) { ...CartFields } }`,
    { cartId }
  );
  return data.cart ? mapRemoteCart(data.cart) : null;
}

/* --------------------------- React hook ---------------------------- */

export function useProduct(handle) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getProductByHandle(handle)
      .then((p) => {
        if (alive) {
          setProduct(p);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setProduct(MOCK_PRODUCTS.find((m) => m.handle === handle) || null);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [handle]);
  return { product, loading };
}
